import { ReactNode, useState } from "react";
import { Button, ButtonColor, ButtonSize, ButtonFill } from "components/Button";
import { Select } from "components/Select";
import Avatar from "components/Avatar";
import { generateInitials } from "util/generateInitials";
import { recentKey, useRecentOptions } from "util/recentPeople";
import { useNavigate } from "react-router-dom";
import SectionTitle from "components/SectionTitle";
import { BubbleTone } from "components/IconBubble";

/** What the section needs of a person: a tutor's student, a student's tutor. */
type Person = { id: number; first_name: string; last_name: string };

const fullName = (person: Person) => `${person.first_name} ${person.last_name}`;

/** The section's title row, with what goes at its right, and its box. */
function Frame({
  id,
  title,
  icon,
  action,
  children,
}: {
  id: string;
  title: string;
  icon: ReactNode;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="student-profile--section" id={id}>
      <div className="student-profile--section-header">
        <SectionTitle icon={icon} tone={BubbleTone.GREEN}>
          {title}
        </SectionTitle>
        {action}
      </div>

      <div className="tutor-profile--section-items max-h-[380px] overflow-y-auto">
        {children}
      </div>
    </section>
  );
}

/**
 * The section before its people are loaded: the app's spinner in the box,
 * or what went wrong if they could not be.
 */
export function AssignedPeopleLoading({
  id,
  title,
  icon,
  error,
}: {
  id: string;
  title: string;
  icon: ReactNode;
  error?: string;
}) {
  return (
    <Frame id={id} title={title} icon={icon}>
      {error ? (
        <div className="tutor-profile--section-item">{error}</div>
      ) : (
        <div className="tutor-profile--section-item flex justify-center">
          <span className="spinner-border" role="status" aria-label="Loading" />
        </div>
      )}
    </Frame>
  );
}

/**
 * A person page's section of the people paired with them: a tutor's
 * students, a student's tutors. Each has remove and view, and the Assign
 * button turns into a picker of the organization's others not paired yet.
 */
export default function AssignedPeople<T extends Person>({
  id,
  title,
  icon,
  noun,
  assigned,
  candidates,
  viewPath,
  assign,
  remove,
  onChange,
}: {
  id: string;
  title: string;
  icon: ReactNode;
  /** One of them, as the Assign button says it: "Student", "Tutor". */
  noun: string;
  /** Who is paired now. */
  assigned: T[];
  /** Everyone who can be picked (those already paired are left out). */
  candidates: T[];
  viewPath: (person: T) => string;
  assign: (person: T) => Promise<unknown>;
  remove: (person: T) => Promise<unknown>;
  /** A pairing was added or removed. */
  onChange?: () => void;
}) {
  const navigate = useNavigate();
  const [people, setPeople] = useState<T[]>(assigned);
  const [adding, setAdding] = useState(false);
  const options = candidates.filter((c) => !people.some((p) => p.id === c.id));
  const kind = noun.toLowerCase();
  // The noun's kind ("student", "tutor") names the recent people to list,
  // asked for only while the picker is out.
  const pickable = useRecentOptions(adding ? [kind] : []);

  async function handleAssign(person: T) {
    await assign(person)
      .then(() => {
        setPeople((current) => [...current, person]);
        onChange?.();
      })
      .catch((err) => {
        console.error(`Unable to assign ${kind} ${person.id}: `, err);
      });
  }

  async function handleRemove(person: T) {
    await remove(person)
      .then(() => {
        setPeople((current) => current.filter((p) => p.id !== person.id));
        onChange?.();
      })
      .catch((err) => {
        console.error(`Unable to remove ${kind} ${person.id}: `, err);
      });
  }

  return (
    <Frame
      id={id}
      title={title}
      icon={icon}
      action={
        adding ? (
          <Select
            id={`add-${kind}`}
            name={`add-${kind}`}
            options={pickable.options(options, (person) =>
              recentKey(kind, person.id)
            )}
            onInputChange={pickable.onInputChange}
            value={null}
            isDisabled={options.length === 0}
            getOptionLabel={(a) => `${a ? fullName(a) : ""}`}
            getOptionValue={(a) => (a ? a.id.toString() : "")}
            onChange={(person: T) => handleAssign(person)}
            className="w-36 h-9 text-sm"
          />
        ) : (
          <div className="flex flex-row gap-1.5 items-center">
            {options.length > 0 && (
              <Button
                color={ButtonColor.SKYBLUE}
                size={ButtonSize.SMALL}
                onClick={() => setAdding(true)}
              >
                Assign {noun}
              </Button>
            )}
          </div>
        )
      }
    >
      {people.length > 0 ? (
        people.map((person) => (
          <div
            key={person.id}
            className="tutor-profile--section-item tutor-detail--student"
          >
            <Avatar
              alt={fullName(person)}
              fallback={generateInitials(fullName(person), 2)}
              color="skyblue"
            />

            <div className="tutor-detail--student-details">
              <p>
                {person.first_name} {person.last_name}
              </p>
              <div>
                <Button
                  color={ButtonColor.SKYBLUE}
                  fill={ButtonFill.HOLLOW}
                  size={ButtonSize.EXTRA_SMALL}
                  onClick={() => handleRemove(person)}
                >
                  remove
                </Button>
                <span className="ml-2">
                  <Button
                    onClick={() => navigate(viewPath(person))}
                    color={ButtonColor.SKYBLUE}
                    size={ButtonSize.EXTRA_SMALL}
                  >
                    view
                  </Button>
                </span>
              </div>
            </div>
          </div>
        ))
      ) : (
        <div className="tutor-profile--section-item">No {kind}s.</div>
      )}
    </Frame>
  );
}
